/**
 * Mock API Interceptor for Netlify (static) deployment.
 * When REACT_APP_USE_MOCK === "true", all axios requests are intercepted
 * and resolved with realistic sample data — no backend needed.
 */
import axios from 'axios';

// ─── helpers ────────────────────────────────────────────────────────────────
const delay = (ms = 200) => new Promise((r) => setTimeout(r, ms));
const uid = () => `mock-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ─── mock datastore (in‑memory, resets on reload) ───────────────────────────
let mockUsers = [
  { id: 'admin-1', email: 'admin@carhipo.com', name: 'Admin', is_admin: true, picture: '', created_at: '2025-01-01T00:00:00Z' },
];
let mockToken = localStorage.getItem('token') || null;
let mockCurrentUser = (() => {
  try { return JSON.parse(localStorage.getItem('mock_current_user')); } catch { return null; }
})();

const MOCK_CARS = [
  { id: 'car-1', title: '2024 BMW M340i xDrive', brand: 'BMW', model: 'M340i', year: 2024, price: 62990, mileage: 8500, condition: 'used', body_type: 'Sedan', fuel_type: 'Gasoline', transmission: 'Automatic', color: 'Alpine White', location: 'Munich, Germany', description: 'Pristine condition with M Sport package, adaptive suspension, and premium sound system.', images: ['https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800'], seller_name: 'Munich Motors', seller_email: 'info@munichmotors.de', seller_phone: '+49 89 1234567', views: 234, is_featured: true, created_at: '2025-06-01T10:00:00Z' },
  { id: 'car-2', title: '2023 Mercedes-AMG C63 S', brand: 'Mercedes-Benz', model: 'C63 AMG', year: 2023, price: 89900, mileage: 15200, condition: 'used', body_type: 'Sedan', fuel_type: 'Gasoline', transmission: 'Automatic', color: 'Obsidian Black', location: 'Stuttgart, Germany', description: 'One owner, full service history. Performance exhaust and carbon fibre package.', images: ['https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800'], seller_name: 'Stuttgart Elite Cars', seller_email: 'sales@sec.de', seller_phone: '+49 711 9876543', views: 456, is_featured: true, created_at: '2025-05-28T14:00:00Z' },
  { id: 'car-3', title: '2025 Tesla Model S Plaid', brand: 'Tesla', model: 'Model S', year: 2025, price: 119990, mileage: 1200, condition: 'new', body_type: 'Sedan', fuel_type: 'Electric', transmission: 'Automatic', color: 'Pearl White', location: 'Berlin, Germany', description: 'Brand new Tesla Model S Plaid with Full Self-Driving capability.', images: ['https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=800'], seller_name: 'EV Berlin', seller_email: 'info@evberlin.de', seller_phone: '+49 30 5551234', views: 789, is_featured: true, created_at: '2025-06-10T09:00:00Z' },
  { id: 'car-4', title: '2024 Porsche 911 Carrera', brand: 'Porsche', model: '911', year: 2024, price: 142500, mileage: 3400, condition: 'used', body_type: 'Coupe', fuel_type: 'Gasoline', transmission: 'Automatic', color: 'Guards Red', location: 'Frankfurt, Germany', description: 'PDK transmission, Sport Chrono package, BOSE surround sound.', images: ['https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=800'], seller_name: 'Porsche Centre Frankfurt', seller_email: 'sales@pcfrankfurt.de', seller_phone: '+49 69 4445678', views: 567, is_featured: true, created_at: '2025-06-05T11:00:00Z' },
  { id: 'car-5', title: '2023 Audi RS6 Avant', brand: 'Audi', model: 'RS6', year: 2023, price: 135000, mileage: 12000, condition: 'used', body_type: 'Wagon', fuel_type: 'Gasoline', transmission: 'Automatic', color: 'Nardo Grey', location: 'Hamburg, Germany', description: 'The ultimate super wagon. Dynamic Package Plus, Carbon optics.', images: ['https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800'], seller_name: 'AutoHaus Hamburg', seller_email: 'info@ahhh.de', seller_phone: '+49 40 3332222', views: 345, is_featured: true, created_at: '2025-05-20T16:00:00Z' },
  { id: 'car-6', title: '2024 Range Rover Sport', brand: 'Land Rover', model: 'Range Rover Sport', year: 2024, price: 98500, mileage: 5600, condition: 'used', body_type: 'SUV', fuel_type: 'Hybrid', transmission: 'Automatic', color: 'Santorini Black', location: 'Düsseldorf, Germany', description: 'HSE Dynamic with air suspension and premium leather interior.', images: ['https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800'], seller_name: 'LR Düsseldorf', seller_email: 'sales@lrdus.de', seller_phone: '+49 211 7778888', views: 123, is_featured: true, created_at: '2025-06-08T08:00:00Z' },
  { id: 'car-7', title: '2025 Volkswagen ID.4 GTX', brand: 'Volkswagen', model: 'ID.4', year: 2025, price: 52990, mileage: 800, condition: 'new', body_type: 'SUV', fuel_type: 'Electric', transmission: 'Automatic', color: 'Moonstone Grey', location: 'Wolfsburg, Germany', description: 'Dual-motor AWD electric SUV with 77 kWh battery.', images: ['https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800'], seller_name: 'VW Factory Store', seller_email: 'direct@vw.de', seller_phone: '+49 5361 9999', views: 90, is_featured: false, created_at: '2025-06-12T13:00:00Z' },
  { id: 'car-8', title: '2022 Toyota Supra GR', brand: 'Toyota', model: 'Supra', year: 2022, price: 58900, mileage: 22000, condition: 'used', body_type: 'Coupe', fuel_type: 'Gasoline', transmission: 'Automatic', color: 'Renaissance Red', location: 'Cologne, Germany', description: '3.0L inline-6, launch edition with heritage livery.', images: ['https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=800'], seller_name: 'Sport Auto Köln', seller_email: 'info@sportak.de', seller_phone: '+49 221 1112233', views: 678, is_featured: false, created_at: '2025-04-15T10:00:00Z' },
];

const MOCK_BRANDS = [
  { id: 'b1', name: 'BMW', country: 'Germany', founded: 1916, description: 'Bayerische Motoren Werke — known for sporty sedans and SUVs.', logo_url: '', models_count: 3 },
  { id: 'b2', name: 'Mercedes-Benz', country: 'Germany', founded: 1926, description: 'Luxury vehicles, buses, coaches and trucks.', logo_url: '', models_count: 4 },
  { id: 'b3', name: 'Audi', country: 'Germany', founded: 1909, description: 'Premium vehicles with quattro AWD technology.', logo_url: '', models_count: 3 },
  { id: 'b4', name: 'Tesla', country: 'USA', founded: 2003, description: 'Electric vehicles and clean energy.', logo_url: '', models_count: 4 },
  { id: 'b5', name: 'Porsche', country: 'Germany', founded: 1931, description: 'High-performance sports cars and SUVs.', logo_url: '', models_count: 3 },
  { id: 'b6', name: 'Volkswagen', country: 'Germany', founded: 1937, description: "The people's car — world's largest automaker.", logo_url: '', models_count: 4 },
  { id: 'b7', name: 'Toyota', country: 'Japan', founded: 1937, description: 'Reliable, innovative vehicles for every segment.', logo_url: '', models_count: 5 },
  { id: 'b8', name: 'Ferrari', country: 'Italy', founded: 1939, description: 'Legendary Italian supercar manufacturer.', logo_url: '', models_count: 2 },
  { id: 'b9', name: 'Land Rover', country: 'UK', founded: 1948, description: 'Premium SUVs and off-road vehicles.', logo_url: '', models_count: 3 },
  { id: 'b10', name: 'Honda', country: 'Japan', founded: 1948, description: 'Reliable cars, motorcycles, and power equipment.', logo_url: '', models_count: 3 },
  { id: 'b11', name: 'Ford', country: 'USA', founded: 1903, description: 'American automotive icon since 1903.', logo_url: '', models_count: 4 },
  { id: 'b12', name: 'Lamborghini', country: 'Italy', founded: 1963, description: 'Extreme super sports cars from Sant\'Agata.', logo_url: '', models_count: 2 },
];

const MOCK_MODELS = [
  { id: 'm1', brand: 'BMW', name: '3 Series', years: [2019,2020,2021,2022,2023,2024], body_types: ['Sedan','Wagon'], description: 'The quintessential sport sedan.', image_url: '' },
  { id: 'm2', brand: 'BMW', name: 'X5', years: [2019,2020,2021,2022,2023,2024], body_types: ['SUV'], description: 'Luxury mid-size SUV.', image_url: '' },
  { id: 'm3', brand: 'Mercedes-Benz', name: 'C-Class', years: [2019,2020,2021,2022,2023,2024], body_types: ['Sedan','Wagon','Coupe'], description: 'Luxury compact executive car.', image_url: '' },
  { id: 'm4', brand: 'Tesla', name: 'Model 3', years: [2019,2020,2021,2022,2023,2024,2025], body_types: ['Sedan'], description: 'Affordable all-electric sedan.', image_url: '' },
  { id: 'm5', brand: 'Tesla', name: 'Model S', years: [2019,2020,2021,2022,2023,2024,2025], body_types: ['Sedan'], description: 'Premium all-electric sedan.', image_url: '' },
  { id: 'm6', brand: 'Porsche', name: '911', years: [2019,2020,2021,2022,2023,2024], body_types: ['Coupe','Convertible'], description: 'Iconic rear-engine sports car.', image_url: '' },
  { id: 'm7', brand: 'Audi', name: 'A4', years: [2019,2020,2021,2022,2023,2024], body_types: ['Sedan','Wagon'], description: 'Premium compact executive car.', image_url: '' },
  { id: 'm8', brand: 'Audi', name: 'RS6', years: [2020,2021,2022,2023,2024], body_types: ['Wagon'], description: 'Super-wagon with V8 twin-turbo.', image_url: '' },
  { id: 'm9', brand: 'Volkswagen', name: 'Golf', years: [2019,2020,2021,2022,2023,2024], body_types: ['Hatchback'], description: 'The best-selling European car ever.', image_url: '' },
  { id: 'm10', brand: 'Land Rover', name: 'Range Rover Sport', years: [2019,2020,2021,2022,2023,2024], body_types: ['SUV'], description: 'Dynamic luxury SUV.', image_url: '' },
];

// ─── pageview analytics store ───────────────────────────────────────────────
let analyticsEvents = [];

// ─── chat session store ─────────────────────────────────────────────────────
let chatMessages = {};

// ─── route matchers ─────────────────────────────────────────────────────────
function matchRoute(method, url) {
  const u = url.replace(/^https?:\/\/[^/]+/, '');   // strip origin
  const p = u.split('?')[0];                         // strip query
  const q = Object.fromEntries(new URLSearchParams(u.split('?')[1] || ''));

  // ── Health ──
  if (method === 'get' && p === '/api/health') return { handler: () => ({ status: 'healthy' }) };

  // ── Auth ──
  if (method === 'post' && p === '/api/auth/login') return { handler: handleLogin };
  if (method === 'post' && p === '/api/auth/register') return { handler: handleRegister };
  if (method === 'get'  && p === '/api/auth/me') return { handler: handleMe };
  if (method === 'post' && p === '/api/auth/logout') return { handler: handleLogout };
  // NOTE: /api/auth/google/session is NOT mocked — it passes through to the real
  // backend so the REAL Google account name, email, and profile picture are used.

  // ── Cars ──
  if (method === 'get'  && p === '/api/cars/featured') return { handler: () => MOCK_CARS };
  if (method === 'get'  && p === '/api/cars') return { handler: () => handleCarsSearch(q) };
  if (method === 'get'  && p.match(/^\/api\/cars\/[\w-]+$/)) return { handler: () => handleCarDetail(p.split('/').pop()) };
  if (method === 'post' && p === '/api/cars') return { handler: handleCreateCar };

  // ── Seed ──
  if (method === 'post' && p === '/api/seed-data') return { handler: () => ({ message: 'Mock data loaded', brands: 12, models: 10, cars: MOCK_CARS.length }) };

  // ── Wiki ──
  if (method === 'get' && p === '/api/wiki/brands') return { handler: () => MOCK_BRANDS };
  if (method === 'get' && p === '/api/wiki/search') return { handler: () => handleWikiSearch(q) };
  if (method === 'get' && p.match(/^\/api\/wiki\/brands\/[^/]+\/models$/)) {
    const brand = decodeURIComponent(p.split('/')[4]);
    return { handler: () => MOCK_MODELS.filter(m => m.brand.toLowerCase() === brand.toLowerCase()) };
  }
  if (method === 'get' && p.match(/^\/api\/wiki\/models\//)) {
    const id = p.split('/').pop();
    return { handler: () => MOCK_MODELS.find(m => m.id === id) || MOCK_MODELS[0] };
  }

  // ── VIN ──
  // ── VIN Check & Decode — NOT MOCKED, uses real Vincario API via backend ──

  // ── Chat ──
  if (method === 'post' && p === '/api/chat') return { handler: handleChat };
  if (method === 'get'  && p.match(/^\/api\/chat\/history\//)) return { handler: () => handleChatHistory(p.split('/').pop()) };

  // ── Analytics ──
  if (method === 'post' && p === '/api/analytics/track') return { handler: handleAnalyticsTrack };

  // ── Admin ──
  if (method === 'get' && p === '/api/admin/stats') return { handler: handleAdminStats };
  if (method === 'get' && p === '/api/admin/analytics/pageviews') return { handler: () => handleAdminPageviews(q) };
  if (method === 'get' && p === '/api/admin/analytics/pages') return { handler: handleAdminPages };
  if (method === 'get' && p === '/api/admin/analytics/users') return { handler: () => mockUsers };
  if (method === 'get' && p === '/api/admin/analytics/activity') return { handler: handleAdminActivity };
  if (method === 'get' && p === '/api/admin/analytics/browsers') return { handler: handleAdminBrowsers };

  // ── Recommendations ──
  if (method === 'get' && p === '/api/recommendations') return { handler: () => MOCK_CARS.slice(0, 4) };

  // ── My Cars ──
  if (method === 'get' && p === '/api/my-cars') return { handler: () => mockCurrentUser ? MOCK_CARS.filter((_, i) => i < 2).map(c => ({ ...c, seller_id: mockCurrentUser.id, seller_name: mockCurrentUser.name })) : [] };
  if (method === 'delete' && p.match(/^\/api\/cars\/[\w-]+$/)) return { handler: () => ({ message: 'Deleted' }) };

  // ── Profile ──
  if (method === 'get' && p.match(/^\/api\/profile\/[\w-]+$/)) {
    return { handler: () => {
      const userId = p.split('/').pop();
      const u = mockUsers.find(x => x.id === userId) || mockCurrentUser || mockUsers[0];
      return { ...u, bio: u.bio || '', phone: u.phone || '', location: u.location || '', website: '', social_links: u.social_links || {}, avatar_url: u.avatar_url || '', cover_url: u.cover_url || '', cars: MOCK_CARS.slice(0, 2), reviews: [], avg_rating: 0, review_count: 0, posts: [], trust_score: 30 };
    }};
  }
  if (method === 'put' && p === '/api/profile') return { handler: (data) => {
    if (mockCurrentUser && data) { Object.assign(mockCurrentUser, data); localStorage.setItem('mock_current_user', JSON.stringify(mockCurrentUser)); }
    return { ...mockCurrentUser, bio: '', phone: '', location: '', website: '', social_links: {}, avatar_url: '', cover_url: '', ...data };
  }};
  if (method === 'post' && p === '/api/profile/avatar') return { handler: () => ({ url: '/mock-avatar.jpg' }) };
  if (method === 'post' && p === '/api/profile/cover') return { handler: () => ({ url: '/mock-cover.jpg' }) };

  // ── VIN Decode — NOT MOCKED, uses real Vincario API via backend ──

  // ── Bulk Import ──
  if (method === 'post' && p === '/api/bulk-import/vin-list') return { handler: (data) => {
    const vins = data?.vins || [];
    const results = vins.map(v => ({ vin: v, status: 'success', car_id: uid(), decoded: { brand: 'BMW', model: 'Mock', year: 2022 } }));
    return { job_id: uid(), total: vins.length, succeeded: vins.length, failed: 0, results };
  }};
  if (method === 'post' && p === '/api/bulk-import/csv') return { handler: () => ({ job_id: uid(), total: 3, succeeded: 3, failed: 0, results: [{ row: 'Row 1', status: 'success', car_id: uid() }, { row: 'Row 2', status: 'success', car_id: uid() }, { row: 'Row 3', status: 'success', car_id: uid() }] }) };
  if (method === 'get' && p === '/api/bulk-import/csv-template') return { handler: () => 'title,brand,model,year,price' };
  if (method === 'post' && p === '/api/bulk-import/api-key') return { handler: () => ({ api_key: 'ch_mock_' + uid() }) };
  if (method === 'post' && p === '/api/bulk-import/api') return { handler: () => ({ total: 1, succeeded: 1, failed: 0, results: [{ title: 'Mock Car', status: 'success', car_id: uid() }] }) };
  if (method === 'get' && p === '/api/import-jobs') return { handler: () => [] };

  // ── Social Feed ──
  if (method === 'post' && p === '/api/posts') return { handler: (data) => ({ id: uid(), user_id: mockCurrentUser?.id, user_name: mockCurrentUser?.name, content: data?.content, media: [], likes: [], like_count: 0, comment_count: 0, created_at: new Date().toISOString() }) };
  if (method === 'get' && p === '/api/posts') return { handler: () => [] };
  if (method === 'post' && p.match(/^\/api\/posts\/[\w-]+\/like$/)) return { handler: () => ({ liked: true, like_count: 1 }) };
  if (method === 'post' && p.match(/^\/api\/posts\/[\w-]+\/comment$/)) return { handler: (data) => ({ id: uid(), user_name: mockCurrentUser?.name, content: data?.content, created_at: new Date().toISOString() }) };
  if (method === 'get' && p.match(/^\/api\/posts\/[\w-]+\/comments$/)) return { handler: () => [] };

  // ── Reviews ──
  if (method === 'post' && p === '/api/reviews') return { handler: (data) => ({ id: uid(), rating: data?.rating, comment: data?.comment, author_name: mockCurrentUser?.name, created_at: new Date().toISOString() }) };

  // ── Favorites ──
  if (method === 'post' && p.match(/^\/api\/favorites\/[\w-]+$/)) return { handler: () => ({ favorited: true }) };
  if (method === 'get' && p === '/api/favorites') return { handler: () => MOCK_CARS.slice(0, 2) };
  if (method === 'get' && p === '/api/favorites/ids') return { handler: () => [] };

  // ── Magazine — NOT MOCKED, uses real AI backend ──

  // ── Saved Searches ──
  if (method === 'post' && p === '/api/saved-searches') return { handler: (data) => ({ id: uid(), name: data?.name, filters: data?.filters, notify: true, created_at: new Date().toISOString() }) };
  if (method === 'get' && p === '/api/saved-searches') return { handler: () => [] };
  if (method === 'delete' && p.match(/^\/api\/saved-searches\/[\w-]+$/)) return { handler: () => ({ deleted: true }) };

  // ── Messaging ──
  if (method === 'post' && p === '/api/conversations') return { handler: (data) => ({ id: uid(), participants: [mockCurrentUser?.id, data?.user_id], participant_names: {}, car_id: data?.car_id, car_title: '', last_message: '', last_message_at: new Date().toISOString(), unread: {}, created_at: new Date().toISOString() }) };
  if (method === 'get' && p === '/api/conversations') return { handler: () => [] };
  if (method === 'get' && p.match(/^\/api\/conversations\/[\w-]+\/messages$/)) return { handler: () => [] };
  if (method === 'post' && p.match(/^\/api\/conversations\/[\w-]+\/messages$/)) return { handler: (data) => ({ id: uid(), sender_id: mockCurrentUser?.id, sender_name: mockCurrentUser?.name, content: data?.content, created_at: new Date().toISOString() }) };
  if (method === 'get' && p === '/api/messages/unread-count') return { handler: () => ({ count: 0 }) };

  // ── File Upload — NOT MOCKED, real backend handles upload + serves files ──
  // ── Profile / Avatar / Cover — NOT MOCKED, real backend persists images ──

  return null;
}

// ─── handlers ───────────────────────────────────────────────────────────────

function handleLogin(data) {
  const { email, password } = data || {};
  const user = mockUsers.find(u => u.email === email);
  if (!user) {
    if (email && password) {
      // auto-create for demo
      const newUser = { id: uid(), email, name: email.split('@')[0], is_admin: false, picture: '', created_at: new Date().toISOString() };
      mockUsers.push(newUser);
      mockToken = 'mock-jwt-' + uid();
      mockCurrentUser = newUser;
      localStorage.setItem('mock_current_user', JSON.stringify(newUser));
      return { access_token: mockToken, user: newUser };
    }
    throw { response: { status: 401, data: { detail: 'Invalid credentials' } } };
  }
  mockToken = 'mock-jwt-' + uid();
  mockCurrentUser = user;
  localStorage.setItem('mock_current_user', JSON.stringify(user));
  return { access_token: mockToken, user };
}

function handleRegister(data) {
  const { name, email, password } = data || {};
  if (mockUsers.find(u => u.email === email)) {
    throw { response: { status: 400, data: { detail: 'Email already registered' } } };
  }
  const newUser = { id: uid(), email, name, is_admin: false, picture: '', created_at: new Date().toISOString() };
  mockUsers.push(newUser);
  mockToken = 'mock-jwt-' + uid();
  mockCurrentUser = newUser;
  localStorage.setItem('mock_current_user', JSON.stringify(newUser));
  return { access_token: mockToken, user: newUser };
}

function handleMe() {
  if (!mockCurrentUser) throw { response: { status: 401, data: { detail: 'Not authenticated' } } };
  return mockCurrentUser;
}

function handleLogout() {
  mockToken = null;
  mockCurrentUser = null;
  localStorage.removeItem('mock_current_user');
  return { message: 'Logged out' };
}

function handleGoogleSession() {
  // In mock mode, simulate a successful Google login
  const user = { id: uid(), email: 'google.user@gmail.com', name: 'Google User', is_admin: false, picture: 'https://lh3.googleusercontent.com/a/default-user', created_at: new Date().toISOString() };
  mockUsers.push(user);
  mockCurrentUser = user;
  localStorage.setItem('mock_current_user', JSON.stringify(user));
  return { user };
}

function handleCarsSearch(query) {
  let results = [...MOCK_CARS];
  if (query.brand) results = results.filter(c => c.brand.toLowerCase().includes(query.brand.toLowerCase()));
  if (query.search) {
    const s = query.search.toLowerCase();
    results = results.filter(c => c.title.toLowerCase().includes(s) || c.brand.toLowerCase().includes(s) || c.model.toLowerCase().includes(s));
  }
  if (query.min_price) results = results.filter(c => c.price >= Number(query.min_price));
  if (query.max_price) results = results.filter(c => c.price <= Number(query.max_price));
  if (query.condition) results = results.filter(c => c.condition === query.condition);
  if (query.body_type) results = results.filter(c => c.body_type === query.body_type);
  if (query.fuel_type) results = results.filter(c => c.fuel_type === query.fuel_type);
  return results;
}

function handleCarDetail(id) {
  const car = MOCK_CARS.find(c => c.id === id);
  if (!car) throw { response: { status: 404, data: { detail: 'Car not found' } } };
  return car;
}

function handleCreateCar(data) {
  const newCar = { ...data, id: uid(), views: 0, is_featured: false, created_at: new Date().toISOString() };
  MOCK_CARS.unshift(newCar);
  return newCar;
}

function handleWikiSearch(query) {
  const q = (query.q || '').toLowerCase();
  return {
    brands: MOCK_BRANDS.filter(b => b.name.toLowerCase().includes(q)),
    models: MOCK_MODELS.filter(m => m.name.toLowerCase().includes(q) || m.brand.toLowerCase().includes(q)),
  };
}

function handleChat(data) {
  const { message, session_id } = data || {};
  const lower = (message || '').toLowerCase();

  if (!chatMessages[session_id]) chatMessages[session_id] = [];
  chatMessages[session_id].push({ role: 'user', content: message, timestamp: new Date().toISOString() });

  let response, action = 'general', resultData = null;

  if (lower.includes('bmw') || lower.includes('find') || lower.includes('search') || lower.includes('show')) {
    const keyword = lower.match(/bmw|mercedes|audi|tesla|porsche|toyota|ford|volkswagen|range rover|honda/i)?.[0] || '';
    const cars = keyword ? MOCK_CARS.filter(c => c.brand.toLowerCase().includes(keyword) || c.title.toLowerCase().includes(keyword)) : MOCK_CARS.slice(0, 4);
    action = 'search_cars';
    resultData = { cars, type: 'cars', count: cars.length };
    response = cars.length > 0
      ? `I found ${cars.length} car${cars.length > 1 ? 's' : ''} for you! Here are the top picks — each one is a great choice. Click any card to see full details.`
      : "I couldn't find any cars matching that. Try a different brand or broader search!";
  } else if (lower.includes('vin') || /[A-Z0-9]{17}/.test(message || '')) {
    const vinMatch = (message || '').match(/[A-Z0-9]{17}/);
    action = 'check_vin';
    response = vinMatch
      ? `I found a VIN: ${vinMatch[0]}. Head to our VIN Decoder page for complete vehicle specifications from the Vincario database!`
      : "To check a VIN, go to our VIN Decoder page and enter the 17-character code. We'll pull real data from the Vincario vehicle database.";
  } else if (lower.includes('wiki') || lower.includes('brand') || lower.includes('tell me about')) {
    const brand = lower.match(/bmw|mercedes|audi|tesla|porsche|toyota|ford|volkswagen|ferrari|lamborghini|honda|land rover/i)?.[0] || '';
    const brands = brand ? MOCK_BRANDS.filter(b => b.name.toLowerCase().includes(brand)) : MOCK_BRANDS.slice(0, 4);
    const models = brand ? MOCK_MODELS.filter(m => m.brand.toLowerCase().includes(brand)) : [];
    action = 'search_wiki';
    resultData = { wiki: { brands, models }, type: 'wiki' };
    response = brands.length > 0
      ? `Here's what I found in our wiki! ${brands[0].name} was founded in ${brands[0].founded} in ${brands[0].country}. ${brands[0].description}`
      : "I have info on many brands — try asking about BMW, Tesla, Porsche, or any major manufacturer!";
  } else if (lower.includes('recommend') || lower.includes('suggest') || lower.includes('budget')) {
    const cars = MOCK_CARS.slice(0, 4);
    action = 'search_cars';
    resultData = { cars, type: 'cars', count: cars.length };
    response = "Based on current trends, here are my top recommendations! Each offers excellent value and performance in its class.";
  } else {
    response = "Hi there! I'm CarHippo AI 🚗 I can help you:\n• **Search cars** — e.g., \"Show me BMW under €80,000\"\n• **Check a VIN** — paste any 17-character VIN\n• **Browse our Wiki** — e.g., \"Tell me about Tesla\"\n• **Get recommendations** — e.g., \"Recommend a family SUV\"";
  }

  const botMsg = { role: 'assistant', content: response, action, data: resultData, timestamp: new Date().toISOString() };
  chatMessages[session_id].push(botMsg);

  return { response, action, data: resultData };
}

function handleChatHistory(sessionId) {
  return chatMessages[sessionId] || [];
}

function handleAnalyticsTrack(data) {
  analyticsEvents.push({ ...data, id: uid(), timestamp: new Date().toISOString(), user_agent: navigator.userAgent });
  return { ok: true };
}

function handleAdminStats() {
  return {
    total_users: mockUsers.length,
    total_cars: MOCK_CARS.length,
    total_pageviews: analyticsEvents.filter(e => e.event_type === 'pageview').length,
    total_sessions: 1,
    today_pageviews: analyticsEvents.filter(e => e.event_type === 'pageview' && e.timestamp?.startsWith(new Date().toISOString().slice(0, 10))).length,
    today_registrations: 0,
    new_cars_week: MOCK_CARS.length,
  };
}

function handleAdminPageviews(query) {
  const days = Number(query.days) || 30;
  const result = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(Date.now() - (days - 1 - i) * 86400000);
    const dateStr = d.toISOString().slice(0, 10);
    const views = analyticsEvents.filter(e => e.event_type === 'pageview' && e.timestamp?.startsWith(dateStr)).length;
    result.push({ date: dateStr, views: views + Math.floor(Math.random() * 5) }); // add some demo variance
  }
  return result;
}

function handleAdminPages() {
  const counts = {};
  analyticsEvents.filter(e => e.event_type === 'pageview').forEach(e => {
    counts[e.page || '/'] = (counts[e.page || '/'] || 0) + 1;
  });
  // Add some demo data if empty
  if (Object.keys(counts).length === 0) {
    return [
      { page: '/', views: 42 }, { page: '/cars', views: 28 },
      { page: '/wiki', views: 15 }, { page: '/vin-check', views: 12 },
      { page: '/login', views: 8 }, { page: '/sell', views: 6 },
    ];
  }
  return Object.entries(counts).map(([page, views]) => ({ page, views })).sort((a, b) => b.views - a.views);
}

function handleAdminActivity() {
  return {
    events: analyticsEvents.slice(-20).reverse(),
    recent_cars: MOCK_CARS.slice(0, 5).map(c => ({ id: c.id, title: c.title, seller_name: c.seller_name, created_at: c.created_at, price: c.price })),
  };
}

function handleAdminBrowsers() {
  const ua = navigator.userAgent.toLowerCase();
  let browser = 'Other';
  if (ua.includes('chrome') && !ua.includes('edg')) browser = 'Chrome';
  else if (ua.includes('firefox')) browser = 'Firefox';
  else if (ua.includes('safari') && !ua.includes('chrome')) browser = 'Safari';
  else if (ua.includes('edg')) browser = 'Edge';
  return [
    { browser, count: analyticsEvents.length || 10 },
    { browser: 'Chrome', count: 45 },
    { browser: 'Safari', count: 20 },
    { browser: 'Firefox', count: 12 },
    { browser: 'Edge', count: 8 },
  ];
}

// ─── install interceptor ────────────────────────────────────────────────────
export function installMockInterceptor() {
  console.log('%c🚗 CarHippo Mock Mode Active', 'color: #00FFFF; font-size: 14px; font-weight: bold;');

  axios.interceptors.request.use(async (config) => {
    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';

    const match = matchRoute(method, url);
    if (!match) return config; // let it pass through (will fail silently)

    await delay(150 + Math.random() * 300); // simulate latency

    try {
      const result = match.handler(config.data);
      // Craft a fake response and abort the real request
      const fakeResponse = {
        data: result,
        status: 200,
        statusText: 'OK (mock)',
        headers: {},
        config,
      };
      return Promise.reject({ __MOCK_RESPONSE__: true, response: fakeResponse });
    } catch (err) {
      if (err.__MOCK_RESPONSE__) throw err;
      // Build error response
      const status = err?.response?.status || 500;
      const data = err?.response?.data || { detail: 'Mock error' };
      return Promise.reject({
        __MOCK_RESPONSE__: true,
        isAxiosError: true,
        response: { data, status, statusText: 'Error (mock)', headers: {}, config },
      });
    }
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.__MOCK_RESPONSE__ && error?.response?.status === 200) {
        // Convert our fake rejection into a resolved response
        return Promise.resolve(error.response);
      }
      if (error?.__MOCK_RESPONSE__) {
        // Real mock error (401, 403, etc.)
        return Promise.reject(error);
      }
      // Real network/CORS error on a passthrough route — reject so callers' try/catch handle it
      return Promise.reject(error);
    }
  );
}
